import { Box, styled } from '@mui/material';
import { ReactElement } from 'react';

export type StatisticsItemProps = {
  bg: string;
  icon: ReactElement;
  mainColor?: string;
  percents: string;
  label: string;
  extraPercents?: string;
  extraIcon?: ReactElement;
  hours?: string;
  /** If true, use widget-optimized sizing */
  insideWidget?: boolean;
};

const PREFIX = 'TMDX-StatisticsItem';
export const statisticsItemClasses = {
  root: `${PREFIX}-root`,
  pieChart: `${PREFIX}-pieChart`,
  container: `${PREFIX}-container`,
  containerBg: `${PREFIX}-containerBg`,
  contentWrapper: `${PREFIX}-contentWrapper`,
  icon: `${PREFIX}-icon`,
  content: `${PREFIX}-content`,
  main: `${PREFIX}-main`,
  mainText: `${PREFIX}-mainText`,
  extra: `${PREFIX}-extra`,
  extraText: `${PREFIX}-extraText`,
  hours: `${PREFIX}-hours`,
  label: `${PREFIX}-label`,
} as const;

type RootProps = { insideWidget?: boolean };

const Root = styled(Box, {
  name: PREFIX,
  overridesResolver: (props, styles) => styles.root,
  shouldForwardProp: prop => prop !== 'insideWidget',
})<RootProps>(({ theme, insideWidget }) => {
  const { breakpoints, utils } = theme;

  // Config per context (default + max in one place)
  const sizingConfig = insideWidget
    ? {
        ringDiameter: { default: 65, max: 132 },
        gapY: { default: 4, max: 10 },
        gapX: { default: 2, max: 8 },
        mainIconSize: { default: 24, max: 34 },
        extraIconSize: { default: 26, max: 30 },
        ringInset: { default: 3, max: 4 },
        mainFontSize: { default: 14, max: 26 },
        extraFontSize: { default: 12, max: 14 },
        hoursFontSize: { default: 12, max: 14 },
        labelFontSize: { default: 12, max: 14 },
        labelMaxWidth: { default: 120, max: 160 },
      }
    : {
        ringDiameter: { default: 120, max: 132 },
        gapY: { default: 10, max: 10 },
        gapX: { default: 8, max: 8 },
        mainIconSize: { default: 40, max: 40 },
        extraIconSize: { default: 36, max: 36 },
        ringInset: { default: 6, max: 6 },
        mainFontSize: { default: 24, max: 26 },
        extraFontSize: { default: 18, max: 18 },
        hoursFontSize: { default: 18, max: 18 },
        labelFontSize: { default: 18, max: 18 },
        labelMaxWidth: { default: 220, max: 220 },
      };

  // Clamp all sizes to the current breakpoint max between md and 4k
  const sizes = Object.fromEntries(
    Object.entries(sizingConfig).map(([key, { default: def, max }]) => [
      key,
      utils.clamp(def, max, 'md-4k'),
    ]),
  );

  const {
    ringDiameter,
    gapY,
    gapX,
    mainIconSize,
    extraIconSize,
    ringInset,
    mainFontSize,
    extraFontSize,
    hoursFontSize,
    labelFontSize,
    labelMaxWidth,
  } = sizes;

  return {
    [`&.${statisticsItemClasses.root}`]: {
      minWidth: 0,
      '--ringDiameter': ringDiameter,
      '--gapY': gapY,
      '--gapX': gapX,
      '--mainIconSize': mainIconSize,
      '--extraIconSize': extraIconSize,
      '--ringInset': ringInset,
      '--mainFontSize': mainFontSize,
      '--extraFontSize': extraFontSize,
      '--hoursFontSize': hoursFontSize,
      '--labelFontSize': labelFontSize,
      '--labelMaxWidth': labelMaxWidth,
    },

    [`.${statisticsItemClasses.pieChart}`]: {
      gap: 'var(--gapY)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      zIndex: 1,
    },

    [`.${statisticsItemClasses.container}`]: {
      position: 'relative',
      isolation: 'isolate',
      width: 'var(--ringDiameter)',
      height: 'var(--ringDiameter)',
      minWidth: 'var(--ringDiameter)',
      minHeight: 'var(--ringDiameter)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      boxSizing: 'border-box',
    },

    [`.${statisticsItemClasses.containerBg}`]: {
      position: 'absolute',
      inset: 0,
      borderRadius: '50%',
      zIndex: -1,
      '&::after': {
        content: '""',
        position: 'absolute',
        inset: 'var(--ringInset)',
        zIndex: 10,
        background: 'black',
        borderRadius: '50%',
        display: 'block',
      },
    },

    [`.${statisticsItemClasses.contentWrapper}`]: {
      display: 'flex',
      height: '100%',
      boxSizing: 'border-box',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 0,
      gap: `calc(var(--gapY) * 0.2)`,
    },

    [`.${statisticsItemClasses.icon}`]: {
      color: '#7F8083',
      '& svg': {
        width: 'auto',
        height: `calc(var(--mainFontSize) * 1.2)`,
        display: 'block',
        color: 'inherit',
      },
    },

    [`.${statisticsItemClasses.content}`]: {
      display: 'flex',
      gap: 'var(--gap-x)',
      alignItems: 'flex-end',
      flexWrap: 'wrap',
      justifyContent: 'center',
      minWidth: 0,
    },

    [`.${statisticsItemClasses.main}`]: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minWidth: 0,
      [`& .${statisticsItemClasses.icon}`]: {
        marginBottom: `calc(var(--mainFontSize) * -0.4)`,
        '& svg': { height: 'var(--mainIconSize)' },
      },
    },

    [`.${statisticsItemClasses.mainText}`]: {
      color: '#DDDDDD',
      fontWeight: 600,
      fontSize: 'var(--fs-main)',
      lineHeight: 1.05,
      whiteSpace: 'nowrap',
      [breakpoints.down('sm')]: {
        // very small screens: tiny nudge up for readability
        fontSize: `calc(var(--mainFontSize) * 0.95)`,
      },
    },

    [`.${statisticsItemClasses.extra}`]: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minWidth: 0,
      [`& .${statisticsItemClasses.icon}`]: {
        marginBottom: `calc(var(--mainFontSize) * -0.3)`,
        '& svg': { height: 'var(--extraIconSize)' },
      },
    },

    [`.${statisticsItemClasses.extraText}`]: {
      fontSize: 'var(--extraFontSize)',
      lineHeight: 1.1,
      paddingBottom: `calc(var(--extraFontSize) * 0.2)`,
      whiteSpace: 'nowrap',
      color: '#BFC3C7',
    },

    [`.${statisticsItemClasses.hours}`]: {
      color: '#DDDDDD',
      fontWeight: 500,
      fontSize: 'var(--hoursFontSize)',
      lineHeight: 1.1,
      whiteSpace: 'nowrap',
    },

    [`.${statisticsItemClasses.label}`]: {
      color: '#A3A7AB',
      fontSize: 'var(--labelFontSize)',
      maxWidth: 'var(--labelMaxWidth)',
      lineHeight: 1.15,
      textAlign: 'center',
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      [breakpoints.down('sm')]: {
        maxWidth: `calc(var(--labelMaxWidth) * 0.95)`,
      },
    },
  };
});

export const StatisticsItem = ({
  bg,
  percents,
  mainColor,
  icon,
  extraPercents,
  extraIcon,
  label,
  hours,
  insideWidget = false,
}: StatisticsItemProps) => {
  const hasExtra = !!(extraIcon || extraPercents);
  const isSingleIcon = !!icon && !extraIcon;

  return (
    <Root className={statisticsItemClasses.root} insideWidget={insideWidget}>
      <div className={statisticsItemClasses.pieChart}>
        <div className={statisticsItemClasses.container}>
          <div
            className={statisticsItemClasses.containerBg}
            style={{ background: bg }}
          />

          <div className={statisticsItemClasses.contentWrapper}>
            {isSingleIcon && (
              <div className={statisticsItemClasses.icon}>{icon}</div>
            )}

            <div className={statisticsItemClasses.content}>
              <div className={statisticsItemClasses.main}>
                {!isSingleIcon && (
                  <div
                    className={statisticsItemClasses.icon}
                    style={{ color: mainColor }}
                  >
                    {icon}
                  </div>
                )}
                <div
                  className={statisticsItemClasses.mainText}
                  style={{ color: mainColor }}
                >
                  {percents}%
                </div>
              </div>

              {hasExtra && (
                <div className={statisticsItemClasses.extra}>
                  {extraIcon && (
                    <div className={statisticsItemClasses.icon}>
                      {extraIcon}
                    </div>
                  )}
                  {extraPercents && (
                    <div className={statisticsItemClasses.extraText}>
                      {extraPercents}%
                    </div>
                  )}
                </div>
              )}
            </div>

            {hours && (
              <div className={statisticsItemClasses.hours}>{hours}h</div>
            )}
          </div>
        </div>

        <div className={statisticsItemClasses.label}>{label}</div>
      </div>
    </Root>
  );
};
